import { apiService } from './apiService';

class SubscriptionService {
  // Get current subscription details
  async getCurrentSubscription() {
    try {
      const response = await apiService.get(
        `/api/v1/membership_plans/current_subscription?_t=${Date.now()}`,
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Get current subscription error:', error);
      return {
        success: false,
        message: error.message || 'Failed to load subscription details',
        error,
      };
    }
  }

  // Get subscription history
  async getSubscriptionHistory() {
    try {
      const response = await apiService.get(
        '/api/v1/membership_plans/subscription_history',
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Get subscription history error:', error);
      return {
        success: false,
        message: error.message || 'Failed to load subscription history',
        error,
      };
    }
  }

  // Get credit balance (updated endpoint)
  async getCreditBalance() {
    try {
      const response = await apiService.get('/api/v1/credits/balance');
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Get credit balance error:', error);
      return {
        success: false,
        message: error.message || 'Failed to load credit balance',
        error,
      };
    }
  }

  // Create Stripe checkout session for subscription
  async createCheckoutSession(planId) {
    try {
      const response = await apiService.post(
        `/api/v1/membership_plans/${planId}/checkout`,
        {},
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Create checkout session error:', error);
      // Handle specific error cases
      const errorMessage =
        error.data?.error ||
        error.message ||
        'Failed to create checkout session';
      return {
        success: false,
        message: errorMessage,
        fallbackAction: error.data?.fallback_action,
        error,
      };
    }
  }

  // Get available credit packages
  async getCreditPackages() {
    try {
      const response = await apiService.get('/api/v1/credits/packages');
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Get credit packages error:', error);
      return {
        success: false,
        message: error.message || 'Failed to load credit packages',
        error,
      };
    }
  }

  // Create Stripe checkout session for credit purchase
  async createCreditCheckoutSession(packageId) {
    try {
      const response = await apiService.post('/api/v1/credits/checkout', {
        package_id: packageId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Create credit checkout session error:', error);
      const errorMessage =
        error.data?.error ||
        error.message ||
        'Failed to create checkout session';
      return {
        success: false,
        message: errorMessage,
        error,
      };
    }
  }

  // Get Stripe customer portal URL
  async getPortalUrl() {
    try {
      const response = await apiService.post(
        '/api/v1/membership_plans/portal',
        {},
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Get portal URL error:', error);
      return {
        success: false,
        message: error.message || 'Failed to access billing portal',
        error,
      };
    }
  }

  // Get credit transactions with pagination
  async getCreditTransactions(page = 1, perPage = 20) {
    try {
      const response = await apiService.get(
        `/api/v1/membership_plans/credit_transactions?page=${page}&per_page=${perPage}`,
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Get credit transactions error:', error);
      return {
        success: false,
        message: error.message || 'Failed to load credit transactions',
        error,
      };
    }
  }

  // Subscribe to a membership plan
  async subscribeToPlan(planId, expiresAt) {
    try {
      const response = await apiService.post(
        `/api/v1/membership_plans/${planId}/subscribe`,
        {
          expires_at: expiresAt,
        },
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Subscribe to plan error:', error);
      return {
        success: false,
        message: error.message || 'Failed to subscribe to plan',
        error,
      };
    }
  }

  // Cancel current subscription
  async cancelSubscription() {
    try {
      const response = await apiService.delete(
        '/api/v1/membership_plans/cancel_subscription',
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      return {
        success: false,
        message: error.message || 'Failed to cancel subscription',
        error,
      };
    }
  }

  // Resume canceled subscription
  async resumeSubscription() {
    try {
      const response = await apiService.post(
        '/api/v1/membership_plans/resume_subscription',
      );
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Resume subscription error:', error);
      return {
        success: false,
        message: error.message || 'Failed to resume subscription',
        error,
      };
    }
  }

  // Helper method to check if user can submit bids
  canSubmitBids(creditBalance) {
    return creditBalance?.can_submit_bids === true;
  }

  // Helper method to check if user has low credits
  hasLowCredits(creditBalance) {
    return creditBalance?.low_credit_warning === true;
  }

  // Helper method to format subscription status
  getSubscriptionStatusLabel(status) {
    const statusLabels = {
      active: 'Active',
      cancelled: 'Cancelled',
      expired: 'Expired',
      pending: 'Pending',
      trial: 'Trial',
    };
    return statusLabels[status] || status;
  }

  // Helper method to get subscription status color
  getSubscriptionStatusColor(status) {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      trial: 'bg-blue-100 text-blue-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  }

  // Helper method to format transaction type
  getTransactionTypeLabel(transactionType) {
    const typeLabels = {
      bid_submission: 'Bid Submission',
      monthly_allocation: 'Monthly Allocation',
      bonus_credits: 'Bonus Credits',
      refund: 'Refund',
      adjustment: 'Adjustment',
      credit_added: 'Credits Added',
      purchase: 'Credits Purchased',
      plan_change: 'Plan Change',
    };
    return typeLabels[transactionType] || transactionType;
  }

  // Helper method to get transaction type color
  getTransactionTypeColor(transactionType, amount) {
    if (amount > 0) {
      return 'text-green-600'; // Credits added
    } else {
      return 'text-red-600'; // Credits spent
    }
  }

  // Helper method to format credit amount
  formatCredits(amount) {
    return Math.abs(amount).toString();
  }

  // Helper method to format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  // Helper method to format date
  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Helper method to format date and time
  formatDateTime(dateString) {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Helper method to format relative time
  formatRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  // Helper method to calculate days until expiration
  getDaysUntilExpiration(expiresAt) {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  // Helper method to check if subscription is expiring soon
  isExpiringSoon(expiresAt, warningDays = 7) {
    const daysUntil = this.getDaysUntilExpiration(expiresAt);
    return daysUntil <= warningDays && daysUntil > 0;
  }

  // Helper method to check if subscription has expired
  isExpired(expiresAt) {
    return this.getDaysUntilExpiration(expiresAt) <= 0;
  }

  // Helper method to calculate credit usage percentage
  getCreditUsagePercentage(used, total) {
    if (total === 0) return 0;
    return Math.round((used / total) * 100);
  }

  // Helper method to get credit usage color based on percentage
  getCreditUsageColor(percentage) {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    if (percentage >= 50) return 'bg-blue-500';
    return 'bg-green-500';
  }

  // Helper method to format subscription period
  formatSubscriptionPeriod(subscribedAt, expiresAt) {
    const start = this.formatDate(subscribedAt);
    const end = this.formatDate(expiresAt);
    return `${start} - ${end}`;
  }

  // Helper method to create default expires_at date (1 month from now)
  getDefaultExpiryDate() {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString();
  }

  // Helper method to validate subscription data
  validateSubscriptionData(subscription) {
    return (
      subscription &&
      subscription.id &&
      subscription.status &&
      subscription.subscribed_at &&
      subscription.expires_at
    );
  }

  // Helper method to validate credit balance data
  validateCreditBalanceData(creditBalance) {
    return (
      creditBalance &&
      typeof creditBalance.current_credits === 'number' &&
      typeof creditBalance.total_earned_credits === 'number' &&
      typeof creditBalance.total_spent_credits === 'number'
    );
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();
